import { createContext, useState, useEffect, ReactNode } from "react";
import { secureStorage } from "@/infrastructure/storage";
import { STORAGE_KEYS } from "@/domain/constants/storage";

/**
 * 設計系統主題上下文介面
 */
export interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

/**
 * 設計系統主題上下文
 * 提供了應用程式的主題狀態和方法
 */
export const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: false,
  toggleTheme: () => {},
});

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * 設計系統主題提供者
 * 提供了應用程式的主題狀態和方法
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  // 取得初始主題
  const getInitialTheme = () => {
    const savedTheme = secureStorage.getItem(STORAGE_KEYS.THEME_MODE);
    if (savedTheme) {
      return savedTheme === "dark";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  };

  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme);

  // 切換主題
  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  // 當主題改變時更新 DOM
  useEffect(() => {
    const root = window.document.documentElement;

    if (isDarkMode) {
      root.classList.add("dark");
      secureStorage.setItem(STORAGE_KEYS.THEME_MODE, "dark");
    } else {
      root.classList.remove("dark");
      secureStorage.setItem(STORAGE_KEYS.THEME_MODE, "light");
    }
  }, [isDarkMode]);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
